import { FC, FormEventHandler, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FileInput } from '../FileInput';
import { sendFilesRequest } from '../../services/http';
import { RequestError } from '../RequestError';
import classes from './InputForm.module.scss';
import { Button, Checkbox, FormControlLabel, TextField } from '@mui/material';
import clsx from 'clsx';
import { Props } from './types';

export const InputForm: FC<Props> = ({ onProcess, onLoading }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [productName, setProductName] = useState('');
  const [productNameError, setProductNameError] = useState('');
  const [brandName, setBrandName] = useState('');
  const [brandNameError, setBrandNameError] = useState('');
  const [upc, setUpc] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRequestError, setRequestError] = useState(false);
  const [purchaseValidation, setPurchaseValidation] = useState(false);

  const recaptchaRef = useRef<any>();
  const isValid = ((brandName && productName && purchaseValidation) || !purchaseValidation) && files.length;

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!productName) {
      setProductNameError('Field required');
    }
    if (!brandName) {
      setBrandNameError('Field required');
    }
    setSubmitted(true);

    if (!isValid) {
      return;
    }

    try {
      onLoading();
      setLoading(true);
      await recaptchaRef.current.executeAsync();
      const data = {
        productName: productName.trim(),
        gtin: upc.trim(),
        brandName: brandName.trim(),
        purchaseValidation,
      };
      const input = await sendFilesRequest(data, files);

      onProcess(input, data);

      setPurchaseValidation(false);
      setSubmitted(false);
      setProductName('');
      setBrandName('');
      setUpc('');
      setFiles([]);
    } catch (e) {
      setRequestError(true);
    } finally {
      setBrandNameError('');
      setProductNameError('');
      setLoading(false);
    }

  }

  if (isRequestError) {
    return (
      <RequestError
        fileNames={files.map((item) => item.name)}
        onBack={() => setRequestError(false)}
        onFileDrop={(input) => {
          setFiles(input);
          setRequestError(false);
        }}
      />
    );
  }

  return (
    <form action="" onSubmit={onSubmit} encType="multipart/form-data">
      <div className={clsx(classes.wrapper, { [classes.filePicked]: isValid })}>
        <div className={classes.left}>
          <FileInput value={files} onChange={setFiles} submitted={submitted} />
        </div>

        <div className={classes.right}>
          <div className={classes.checkboxWrapper}>
            <FormControlLabel
              control={<Checkbox
                checked={purchaseValidation}
                name="purchaseValidation"
                onChange={(e, checked) => setPurchaseValidation(checked)}
              />}
              label={<span className={classes.title}>Verify the purchase of a specific product (optional)</span>}
            />
          </div>
          <div className={clsx(classes.productNameContainer, { [classes.disabled]: !purchaseValidation })}>
            <TextField
              disabled={!purchaseValidation}
              fullWidth
              name="productName"
              label="product name"
              value={productName}
              onChange={({ target }) => {
                setProductName(target.value);
                setProductNameError(target.value ? '' : 'Field required');
              }}
              error={Boolean(productNameError)}
            />
          </div>
          <div className={clsx(classes.brandNameContainer, { [classes.disabled]: !purchaseValidation })}>
            <TextField
              disabled={!purchaseValidation}
              fullWidth
              name="brandName"
              label="brand"
              value={brandName}
              onChange={({ target }) => {
                setBrandName(target.value);
                setBrandNameError(target.value ? '' : 'Field required');
              }}
              error={Boolean(brandNameError)}
            />
          </div>
          <div className={clsx(classes.upcContainer, { [classes.disabled]: !purchaseValidation })}>
            <TextField
              disabled={!purchaseValidation}
              fullWidth
              label="UPC (Optional)"
              variant="outlined"
              name="upc"
              value={upc}
              type="number"
              onChange={({target}) => {
                setUpc(target.value);
              }}
            />
          </div>

            {loading ? (
              <Button variant="outlined" onClick={(e) => e.preventDefault()}>
                Processing...
              </Button>
            ) : (
              <Button disabled={!isValid} variant="contained" type="submit">Start</Button>
            )}
        </div>
      </div>
      <ReCAPTCHA
        style={{ display: "inline-block" }}
        theme="light"
        size="invisible"
        ref={recaptchaRef}
        sitekey={'6Lci1xgpAAAAAGGy-drsSPyzU7I3d0ZZnaawDaAd'}
      />
    </form>
  );
}